import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
    Button,
    ConfirmDialog,
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Input,
    gtagEvent,
    vars,
} from '@agendor/ui';
import { Modal } from '../components/Modal';
import { Checkbox, CheckboxLabel, FormGroup, InputGroup, Select, TextArea } from '../components/Input';
import BusinessService, { BusinessServiceItem, Intake } from '../business-service';
import { useListModalRoute } from '../navigation';
import { useToast } from '../toast-context';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
    max-width: 800px;
    margin: 0 auto;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
`;

const Title = styled.h1`
    font-size: 1.5rem;
    color: ${vars.foregroundColor};
    margin: 0;
    font-weight: 600;
`;

const ServiceList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const ServiceCard = styled.div`
    background-color: #fff;
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
    padding: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
    cursor: pointer;

    &:hover {
        border-color: ${vars.foregroundColor};
        background-color: ${vars.backgroundColorBright};
    }

    @media (max-width: 600px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
    }
`;

const ServiceInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ServiceName = styled.h3`
    margin: 0;
    font-size: 1.1rem;
    color: ${vars.foregroundColor};
    font-weight: 600;
`;

const ServiceDescription = styled.p`
    margin: 0;
    font-size: 0.95rem;
    color: #666;
    line-height: 1.5;
`;

const ServiceDetails = styled.div`
    display: flex;
    align-items: center;
    gap: 24px;
    flex-shrink: 0;

    @media (max-width: 600px) {
        width: 100%;
        justify-content: space-between;
    }
`;

const DetailItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;

    @media (max-width: 600px) {
        align-items: flex-start;
    }
`;

const DetailLabel = styled.span`
    font-size: 0.8rem;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 500;
`;

const DetailValue = styled.span`
    font-size: 1.1rem;
    color: ${vars.foregroundColor};
    font-weight: 600;
`;

const ModalWrapper = styled.div<{ hideOnDesktop?: boolean }>`
    ${(p) =>
        p.hideOnDesktop &&
        `
        @media (min-width: 601px) {
            display: none;
        }
    `}
`;

const IntakePreviewButton = styled(Button)`
    border-color: ${vars.dividerColor};

    &:not(:disabled):hover {
        border-color: ${vars.foregroundColor};
    }
`;

const EMPTY_SEGMENTS: string[] = [];

export default function ServicesSection({
    service,
    services: initialServices,
    intakes,
    isReady,
    businessLoadError,
    onReloadBusiness,
    isLoadingBusiness,
    isActive,
    modalSegments,
    onNavigate,
    onReload,
}: {
    service: BusinessService;
    services: BusinessServiceItem[];
    /** `undefined` when the business has no intake plan; otherwise (possibly empty) list. */
    intakes: Intake[] | undefined;
    isReady: boolean;
    businessLoadError: string | null;
    onReloadBusiness: () => void;
    isLoadingBusiness: boolean;
    isActive: boolean;
    modalSegments: string[];
    onNavigate: (segments: string[], replace?: boolean) => void;
    onReload: () => void;
}) {
    const { showToast } = useToast();
    const [services, setServices] = useState<BusinessServiceItem[]>(initialServices);
    const [routeReady, setRouteReady] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        onConfirm: () => void;
    }>({ isOpen: false, title: '', message: '', onConfirm: () => {} });

    const [editingService, setEditingService] = useState<(Omit<BusinessServiceItem, 'id'> & { id?: number }) | null>(
        null,
    );
    const [previewIntakeId, setPreviewIntakeId] = useState<number | null>(null);

    useEffect(() => {
        setServices(initialServices);
        const hasData = initialServices.length > 0 || modalSegments.length === 0 || modalSegments[0] === 'new';
        setRouteReady(isReady && hasData);
    }, [initialServices, isReady, modalSegments]);

    const findById = useCallback((id: string) => services.find((s) => String(s.id) === id), [services]);

    const {
        state: modalState,
        error: modalRouteError,
        openItem,
        openNew,
        close,
    } = useListModalRoute<BusinessServiceItem>({
        isActive,
        ready: routeReady,
        segments: modalSegments,
        onNavigate,
        findById,
        label: 'serviços',
        baseSegments: EMPTY_SEGMENTS,
    });

    useEffect(() => {
        if (modalState.kind === 'none') {
            setEditingService(null);
            return;
        }
        if (modalState.kind === 'new') {
            setEditingService({
                id: undefined,
                name: '',
                description: '',
                priceInCents: 0,
                durationInMinutes: 0,
                intakeId: intakes !== undefined ? null : undefined,
            });
            return;
        }
        setEditingService(modalState.item);
    }, [modalState]);

    const handleDeleteClick = () => {
        if (!editingService || !editingService.id) return;
        gtagEvent('services_delete_click');

        const serviceToDelete = editingService;

        setConfirmDialog({
            isOpen: true,
            title: 'Remover Serviço',
            message: 'Tem certeza que deseja remover este serviço? Esta ação não pode ser desfeita.',
            onConfirm: async () => {
                try {
                    setIsSaving(true);
                    await service.deleteService({ serviceId: serviceToDelete.id! });
                    showToast('Serviço removido com sucesso!', 'success', 'app');
                    setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} });
                    close();
                    onReload();
                } catch (error) {
                    console.error('Failed to delete service', error);
                    showToast('Não foi possível remover o serviço.', 'error', 'app');
                } finally {
                    setIsSaving(false);
                }
            },
        });
    };

    const handleSave = async () => {
        if (!editingService) return;
        gtagEvent('services_save_click');
        if (!editingService.name) {
            showToast('Nome é obrigatório.', 'error', 'modal');
            return;
        }
        if (editingService.priceInCents == null) {
            showToast('Valor é obrigatório.', 'error', 'modal');
            return;
        }
        if (editingService.priceInCents < 0) {
            showToast('Valor deve ser maior ou igual a zero.', 'error', 'modal');
            return;
        }
        if (!editingService.durationInMinutes) {
            showToast('Duração é obrigatória.', 'error', 'modal');
            return;
        }
        if (editingService.durationInMinutes <= 0) {
            showToast('Duração deve ser maior que zero.', 'error', 'modal');
            return;
        }

        setIsSaving(true);
        try {
            if (modalState.kind === 'new') {
                await service.createService({
                    name: editingService.name,
                    description: editingService.description ?? '',
                    priceInCents: editingService.priceInCents ?? 0,
                    durationInMinutes: editingService.durationInMinutes ?? 0,
                    intakeId: editingService.intakeId,
                });
                showToast('Serviço criado com sucesso!', 'success', 'app');
            } else if (modalState.kind === 'item') {
                await service.updateService({
                    serviceId: Number(modalState.id),
                    name: editingService.name,
                    description: editingService.description ?? '',
                    priceInCents: editingService.priceInCents ?? 0,
                    durationInMinutes: editingService.durationInMinutes ?? 0,
                    intakeId: editingService.intakeId,
                });
                showToast('Serviço atualizado com sucesso!', 'success', 'app');
            }
            close();
            onReload();
        } catch (error) {
            console.error('Failed to save service', error);
            showToast('Não foi possível salvar o serviço.', 'error', 'modal');
        } finally {
            setIsSaving(false);
        }
    };

    const handleInputChange = (field: keyof BusinessServiceItem, value: any) => {
        setEditingService((prev) => (prev ? { ...prev, [field]: value } : prev));
    };

    const handleDurationBlur = () => {
        if (!editingService?.durationInMinutes) return;
        const rounded = Math.round(editingService.durationInMinutes / 5) * 5;
        if (rounded !== editingService.durationInMinutes) {
            handleInputChange('durationInMinutes', Math.max(5, rounded));
        }
    };

    const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Extract only digits
        const digits = e.target.value.replace(/\D/g, '');
        const cents = digits === '' ? 0 : parseInt(digits, 10);
        handleInputChange('priceInCents', cents);
    };

    const formatPriceInput = (cents: number | null | undefined): string => {
        if (cents == null || cents === 0) return '';
        const reais = Math.floor(cents / 100);
        const centavos = cents % 100;
        return `${reais},${centavos.toString().padStart(2, '0')}`;
    };

    const formatPrice = (cents: number) =>
        new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(cents / 100);

    return (
        <Container>
            <Header>
                <Title>Serviços</Title>
                {modalRouteError && (
                    <div
                        style={{
                            padding: '8px 10px',
                            borderRadius: 6,
                            border: `1px solid ${vars.dangerColorDark}`,
                            background: vars.dangerTintSoft,
                            color: vars.foregroundColor,
                        }}
                    >
                        {modalRouteError}
                    </div>
                )}
                <Button
                    primary
                    data-testid="new-service-btn"
                    onClick={() => {
                        gtagEvent('services_new_click');
                        openNew();
                    }}
                >
                    Novo Serviço
                </Button>
            </Header>

            {businessLoadError ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                    <div style={{ marginBottom: '12px', color: vars.foregroundColor }}>{businessLoadError}</div>
                    <Button onClick={onReloadBusiness} disabled={isLoadingBusiness}>
                        {isLoadingBusiness ? 'Carregando...' : 'Tentar novamente'}
                    </Button>
                </div>
            ) : !isReady ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Carregando serviços...</div>
            ) : (
                <ServiceList>
                    {services.length > 0 ? (
                        services.map((service) => (
                            <ServiceCard
                                key={service.id}
                                onClick={() => {
                                    gtagEvent('services_item_click', { item_id: service.id });
                                    openItem(String(service.id));
                                }}
                            >
                                <ServiceInfo>
                                    <ServiceName>{service.name}</ServiceName>
                                    <ServiceDescription>{service.description}</ServiceDescription>
                                </ServiceInfo>

                                <ServiceDetails>
                                    <DetailItem>
                                        <DetailLabel>Duração</DetailLabel>
                                        <DetailValue>{service.durationInMinutes} min</DetailValue>
                                    </DetailItem>
                                    <DetailItem>
                                        <DetailLabel>Valor</DetailLabel>
                                        <DetailValue className="price">{formatPrice(service.priceInCents)}</DetailValue>
                                    </DetailItem>
                                </ServiceDetails>
                            </ServiceCard>
                        ))
                    ) : (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                            Nenhum serviço cadastrado.
                        </div>
                    )}
                </ServiceList>
            )}

            {editingService && (
                <ModalWrapper hideOnDesktop={confirmDialog.isOpen}>
                    <Modal
                        isOpen
                        onClose={close}
                        title={editingService.id ? 'Editar Serviço' : 'Novo Serviço'}
                        data-testid="service-modal"
                        footer={
                            <>
                                <Button
                                    data-testid="modal-cancel"
                                    onClick={() => {
                                        gtagEvent('services_cancel_click');
                                        close();
                                    }}
                                    disabled={isSaving}
                                >
                                    Cancelar
                                </Button>
                                {editingService.id && (
                                    <Button
                                        data-testid="modal-delete"
                                        danger
                                        onClick={handleDeleteClick}
                                        disabled={isSaving}
                                    >
                                        Remover
                                    </Button>
                                )}
                                <Button
                                    data-testid="modal-save"
                                    primary
                                    type="submit"
                                    form="service-form"
                                    disabled={isSaving}
                                >
                                    {isSaving ? 'Salvando...' : 'Salvar'}
                                </Button>
                            </>
                        }
                    >
                        <form
                            id="service-form"
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleSave();
                            }}
                        >
                            <FormGroup>
                                <InputGroup>
                                    Nome
                                    <Input
                                        type="text"
                                        value={editingService.name || ''}
                                        onChange={(e) => handleInputChange('name', e.target.value)}
                                        placeholder="Ex.: Corte de Cabelo"
                                        maxLength={80}
                                        disabled={isSaving}
                                    />
                                </InputGroup>
                                <InputGroup>
                                    Descrição
                                    <TextArea
                                        value={editingService.description || ''}
                                        onChange={(e) => handleInputChange('description', e.target.value)}
                                        placeholder="Descrição do serviço..."
                                        style={{ minHeight: '80px' }}
                                        disabled={isSaving}
                                    />
                                </InputGroup>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    <InputGroup>
                                        Valor (R$)
                                        <Input
                                            type="text"
                                            inputMode="numeric"
                                            value={formatPriceInput(editingService.priceInCents)}
                                            onChange={handlePriceChange}
                                            placeholder="0,00"
                                            disabled={isSaving}
                                        />
                                    </InputGroup>
                                    <InputGroup>
                                        Duração (minutos)
                                        <Input
                                            type="number"
                                            value={editingService.durationInMinutes || ''}
                                            onChange={(e) =>
                                                handleInputChange('durationInMinutes', Number(e.target.value))
                                            }
                                            onBlur={handleDurationBlur}
                                            placeholder="30"
                                            step="5"
                                            min="5"
                                            disabled={isSaving}
                                        />
                                    </InputGroup>
                                </div>

                                {intakes !== undefined && (
                                    <>
                                        <CheckboxLabel>
                                            <Checkbox
                                                checked={editingService.intakeId != null}
                                                onChange={(e) =>
                                                    handleInputChange(
                                                        'intakeId',
                                                        e.target.checked ? (intakes[0]?.id ?? null) : null,
                                                    )
                                                }
                                                disabled={isSaving || intakes.length === 0}
                                                data-testid="service-intake-toggle"
                                            />
                                            Formulário
                                        </CheckboxLabel>
                                        {intakes.length === 0 && (
                                            <span style={{ fontSize: '0.8rem', color: '#888', marginLeft: 24 }}>
                                                Crie um formulário em Configurações para vincular.
                                            </span>
                                        )}
                                        {editingService.intakeId != null && (
                                            <InputGroup>
                                                Formulário vinculado
                                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                    <Select
                                                        value={editingService.intakeId}
                                                        onChange={(e) =>
                                                            handleInputChange('intakeId', Number(e.target.value))
                                                        }
                                                        disabled={isSaving}
                                                        data-testid="service-intake-select"
                                                        style={{ flex: 1 }}
                                                    >
                                                        {intakes.map((intake) => (
                                                            <option key={intake.id} value={intake.id}>
                                                                {intake.name}
                                                            </option>
                                                        ))}
                                                    </Select>
                                                    <IntakePreviewButton
                                                        type="button"
                                                        iconOnly
                                                        onClick={() =>
                                                            editingService.intakeId != null &&
                                                            setPreviewIntakeId(editingService.intakeId)
                                                        }
                                                        disabled={isSaving}
                                                        aria-label="Ver formulário"
                                                        data-testid="service-intake-preview"
                                                    >
                                                        <EyeIcon />
                                                    </IntakePreviewButton>
                                                </div>
                                            </InputGroup>
                                        )}
                                    </>
                                )}
                            </FormGroup>
                        </form>
                    </Modal>
                </ModalWrapper>
            )}

            <ConfirmDialog
                isOpen={confirmDialog.isOpen}
                title={confirmDialog.title}
                message={confirmDialog.message}
                confirmText="Remover"
                onConfirm={confirmDialog.onConfirm}
                onCancel={() => setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} })}
                isLoading={isSaving}
            />

            <IntakePreviewDialog
                intake={
                    previewIntakeId !== null ? ((intakes ?? []).find((i) => i.id === previewIntakeId) ?? null) : null
                }
                onClose={() => setPreviewIntakeId(null)}
            />
        </Container>
    );
}

/** Outline eye glyph from the design mockup (Lucide-style stroke). */
const EyeIcon = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);

function IntakePreviewDialog({ intake, onClose }: { intake: Intake | null; onClose: () => void }) {
    const typeLabel = (type: Intake['questions'][number]['type']) => {
        switch (type) {
            case 'text':
                return 'Texto';
            case 'textarea':
                return 'Texto longo';
            case 'boolean':
                return 'Sim ou Não';
            case 'enum':
                return 'Escolha';
        }
    };
    return (
        <Dialog isOpen={intake !== null} onClose={onClose} data-testid="intake-preview-dialog">
            <DialogHeader>
                <DialogTitle>{intake?.name ?? ''}</DialogTitle>
            </DialogHeader>
            <DialogContent>
                <ol style={{ paddingLeft: 20, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {intake?.questions.map((q) => (
                        <li key={q.id}>
                            <div style={{ fontSize: '0.95rem', color: vars.foregroundColor }}>
                                {q.questionText}
                                {q.required && <span style={{ color: vars.dangerColor }}> *</span>}
                            </div>
                            <div style={{ fontSize: '0.8rem', color: '#666', marginTop: 2 }}>
                                {typeLabel(q.type)}
                                {q.type === 'enum' && q.options && q.options.length > 0 && (
                                    <> · {q.options.join(', ')}</>
                                )}
                            </div>
                        </li>
                    ))}
                </ol>
            </DialogContent>
            <DialogFooter>
                <Button onClick={onClose}>Fechar</Button>
            </DialogFooter>
        </Dialog>
    );
}
